/**
 * 贝盈：工作模式卡片（对应 getSensorInfo 返回的 workMode）
 * UI 风格与匠盟的传感器模式卡片一致，只是多了一档“高速模式”
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  officeWorkModeImg,
  highspeedWorkModeImg,
  esportWorkModeImg,
  berserkWorkModeImg,
} from "@eweadn-qiankun/shared-assets";
import { Iconfont, useMessage } from "@eweadn-qiankun/shared-components";
import "./WorkModeCard.scss";
// i18next-extract-mark-ns-start mouse-workmode-dynamic
// Dynamic keys used in this file:
// t("mouse.performance.workMode_office")
// t("mouse.performance.workMode_office_desc")
// t("mouse.performance.workMode_hyper")
// t("mouse.performance.workMode_hyper_desc")
// t("mouse.performance.workMode_gaming")
// t("mouse.performance.workMode_gaming_desc")
// t("mouse.performance.workMode_violent")
// t("mouse.performance.workMode_violent_desc")
// i18next-extract-mark-ns-end mouse-workmode-dynamic

export type WorkModeValue = number;

interface WorkModeCardProps {
  workMode: WorkModeValue;
  onWorkModeChange: (mode: WorkModeValue) => void;
  disabledModes?: WorkModeValue[];
}

/** 原相协议 WorkMode 枚举：0 办公 1 高速 2 游戏 3 狂暴 */
const WORK_MODE_OPTIONS: Array<{
  value: WorkModeValue;
  labelKey: string;
  img: string;
  icon: string;
  descKey: string;
  className: string;
}> = [
  {
    value: 0,
    labelKey: "mouse.performance.workMode_office",
    img: officeWorkModeImg,
    icon: "officemode",
    descKey: "mouse.performance.workMode_office_desc",
    className: "mode-office",
  },
  {
    value: 1,
    labelKey: "mouse.performance.workMode_hyper",
    img: highspeedWorkModeImg,
    icon: "highspeedmode",
    descKey: "mouse.performance.workMode_hyper_desc",
    className: "mode-highspeed",
  },
  {
    value: 2,
    labelKey: "mouse.performance.workMode_gaming",
    img: esportWorkModeImg,
    icon: "esportmode",
    descKey: "mouse.performance.workMode_gaming_desc",
    className: "mode-gaming",
  },
  {
    value: 3,
    labelKey: "mouse.performance.workMode_violent",
    img: berserkWorkModeImg,
    icon: "berserkmode",
    descKey: "mouse.performance.workMode_violent_desc",
    className: "mode-berserk",
  },
];

const WorkModeCard: React.FC<WorkModeCardProps> = ({
  workMode,
  onWorkModeChange,
  disabledModes = [],
}) => {
  const { t } = useTranslation();
  const toast = useMessage();
  const disabledSet = new Set(disabledModes);

  const onModeButtonClick = (value: WorkModeValue) => {
    if (disabledSet.has(value)) {
      toast.info(t("mouse.performance.workModeUnavailable"));
      return;
    }
    if (workMode === value) {
      toast.info(t("mouse.performance.sensorModeAlreadyActive"));
      return;
    }
    onWorkModeChange(value);
  };

  return (
    <div className="work-mode-card">
      <div className="card-header">
        <h3 className="card-title">{t("mouse.performance.workMode")}</h3>
      </div>
      <div className="work-mode-content">
        <div className="mode-options">
          {WORK_MODE_OPTIONS.map((opt) => {
            const lockedByList = disabledSet.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                className={`mode-button ${opt.className} ${
                  workMode === opt.value ? "active" : ""
                }`}
                aria-disabled={lockedByList}
                onClick={() => onModeButtonClick(opt.value)}
              >
                <img
                  src={opt.img}
                  alt={t(opt.labelKey)}
                  className="mode-bg-img"
                />
                <Iconfont icon="check" className="mode-check-icon" />
                <div className="mode-text-container">
                  <div className="mode-icon-placeholder">
                    <Iconfont icon={opt.icon} className="mode-icon" />
                  </div>
                  <div className="mode-label">{t(opt.labelKey)}</div>
                  <div className="mode-description">{t(opt.descKey)}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkModeCard;
